import { createPortal } from 'react-dom';
import { makeStyles } from '@material-ui/core/styles';
import { ChevronRightIcon, CrossSmallIcon } from '../../../components/icons';
import type { DocsNavItem, DocsNavSheetProps, DocsNavTab } from './types';

/*
 * The phone docs-navigation sheet (mock 3d): a bottom sheet over the reader with
 * two tabs — the doc set's page tree, and the current page's headings.
 *
 * Presentational. It renders into `document.body` rather than in place: the
 * sheet is fixed to the viewport, and both surfaces mounting it sit deep inside
 * chrome — the reader's subheader, the ADR entity tab — whose ancestors it
 * should not have to trust never to become a containing block. The portal also
 * keeps the scrim clear of `.hone-adr`, whose stylesheet rewrites anything that
 * looks like a MUI surface.
 */
const useStyles = makeStyles({
  root: {
    position: 'fixed',
    inset: 0,
    // Over the bottom tab bar (1400), which the sheet covers.
    zIndex: 1500,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
  },
  scrim: {
    position: 'absolute',
    inset: 0,
    border: 'none',
    padding: 0,
    background: 'rgba(32, 32, 32, 0.4)',
    cursor: 'pointer',
  },
  sheet: {
    position: 'relative',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    // A long tree scrolls inside the sheet rather than under the top app bar.
    maxHeight: '76vh',
    borderRadius: '20px 20px 0 0',
    background: 'var(--bui-white)',
    boxShadow: '0 -16px 48px rgba(0, 0, 0, 0.25)',
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 26px)',
  },
  grabber: {
    flex: 'none',
    width: 36,
    height: 4,
    margin: '10px auto 4px auto',
    borderRadius: 999,
    background: 'var(--hone-portal-hairline-strong)',
  },
  header: {
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    padding: '0 8px 0 24px',
  },
  titles: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  title: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: 'var(--bui-fg-primary)',
  },
  subtitle: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    color: 'var(--bui-fg-secondary)',
  },
  close: {
    display: 'flex',
    flex: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    // 44px tap target, as the rest of the mobile chrome uses.
    width: 44,
    height: 44,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
  },
  closeIcon: {
    width: 18,
    height: 18,
    opacity: 0.55,
  },
  tabs: {
    flex: 'none',
    display: 'flex',
    gap: 4,
    padding: '6px 16px 0 16px',
    borderBottom: '1px solid var(--hone-portal-hairline-soft)',
  },
  tab: {
    position: 'relative',
    border: 'none',
    padding: '10px 14px',
    background: 'none',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    fontWeight: 400,
    color: 'var(--bui-fg-secondary)',
    cursor: 'pointer',
  },
  // The underline overhangs the strip's own hairline by the hairline's width.
  tabActive: {
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 14,
      right: 14,
      bottom: -1,
      height: 2.5,
      background: 'var(--helix-colors-brand-500)',
    },
  },
  list: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    padding: '10px 12px 0 12px',
    overflowY: 'auto',
  },
  item: {
    position: 'relative',
    display: 'flex',
    flex: 'none',
    alignItems: 'center',
    minHeight: 44,
    border: 'none',
    borderRadius: 9,
    padding: '6px 12px 6px 0',
    background: 'none',
    textAlign: 'left',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.4,
    color: 'var(--bui-fg-secondary)',
    cursor: 'pointer',
  },
  // A row that only labels the subtree under it reads as a heading, as the
  // desktop doc nav's section labels do.
  itemSection: {
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
  },
  itemActive: {
    background: 'var(--hone-portal-fill-selected)',
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 12,
      bottom: 12,
      width: 3,
      borderRadius: 2,
      background: 'var(--helix-colors-brand-500)',
    },
  },
  footer: {
    display: 'flex',
    flex: 'none',
    alignItems: 'center',
    gap: 4,
    height: 48,
    marginTop: 10,
    border: 'none',
    borderTop: '1px solid var(--hone-portal-hairline-soft)',
    padding: '0 24px',
    background: 'none',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13.5,
    fontWeight: 500,
    color: 'var(--bui-fg-primary)',
    cursor: 'pointer',
  },
  footerIcon: {
    width: 13,
    height: 13,
    opacity: 0.55,
  },
});

const TAB_LABELS: Record<DocsNavTab, string> = {
  contents: 'Contents',
  onThisPage: 'On this page',
};

// Top-level rows sit on the list's own gutter; each level steps in 16px.
const indentOf = (item: DocsNavItem) => 14 + (Math.max(item.depth, 1) - 1) * 16;

export const DocsNavSheet = ({
  open,
  title,
  subtitle,
  tab,
  onTabChange,
  contents,
  headings,
  onClose,
  onAllDocs,
}: DocsNavSheetProps) => {
  const classes = useStyles();

  if (!open) return null;

  /*
   * A surface can supply one list and not the other — a page with no headings, a
   * doc set of one page — so the tab strip only offers the lists that have rows,
   * and a tab whose list emptied falls back to the other rather than showing an
   * empty sheet.
   */
  const lists: Record<DocsNavTab, DocsNavItem[]> = {
    contents,
    onThisPage: headings,
  };
  const tabs = (Object.keys(TAB_LABELS) as DocsNavTab[]).filter(
    candidate => lists[candidate].length > 0,
  );
  if (tabs.length === 0) return null;
  const active = tabs.includes(tab) ? tab : tabs[0];

  return createPortal(
    <div className={classes.root}>
      <button
        type="button"
        className={classes.scrim}
        aria-label="Close contents"
        onClick={onClose}
      />
      <div className={classes.sheet} role="dialog" aria-modal="true">
        <span className={classes.grabber} />
        <div className={classes.header}>
          <div className={classes.titles}>
            <span className={classes.title}>{title}</span>
            {subtitle && <span className={classes.subtitle}>{subtitle}</span>}
          </div>
          <button
            type="button"
            className={classes.close}
            aria-label="Close contents"
            onClick={onClose}
          >
            <CrossSmallIcon className={classes.closeIcon} />
          </button>
        </div>
        <div className={classes.tabs} role="tablist">
          {tabs.map(candidate => (
            <button
              key={candidate}
              type="button"
              role="tab"
              aria-selected={candidate === active}
              className={`${classes.tab} ${
                candidate === active ? classes.tabActive : ''
              }`}
              onClick={() => onTabChange(candidate)}
            >
              {TAB_LABELS[candidate]}
            </button>
          ))}
        </div>
        <nav className={classes.list} aria-label={TAB_LABELS[active]}>
          {lists[active].map(item => (
            <button
              key={item.id}
              type="button"
              className={`${classes.item} ${
                item.isSection ? classes.itemSection : ''
              } ${item.active ? classes.itemActive : ''}`}
              style={{ paddingLeft: indentOf(item) }}
              onClick={() => {
                item.onSelect();
                onClose();
              }}
            >
              {item.label}
            </button>
          ))}
        </nav>
        {onAllDocs && (
          <button type="button" className={classes.footer} onClick={onAllDocs}>
            All documentation
            <ChevronRightIcon className={classes.footerIcon} />
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
};
